import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../lib/api';
import { SEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  AlertTriangle, Shield, Clock, CheckCircle, XCircle, 
  MessageSquare, FileText, ChevronRight, Plus, ArrowLeft,
  Scale, User, Calendar, DollarSign, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

// Status badge colors
const STATUS_COLORS = {
  open: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  awaiting_response: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  under_review: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  resolved: 'bg-green-500/20 text-green-400 border-green-500/30',
  closed: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
};

const REASON_LABELS = {
  item_not_received: 'Item Not Received',
  item_not_as_described: 'Item Not As Described',
  payment_issue: 'Payment Issue',
  seller_unresponsive: 'Seller Unresponsive',
  buyer_unresponsive: 'Buyer Unresponsive',
  fraud_suspected: 'Fraud Suspected',
  other: 'Other'
};

export default function Disputes() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [disputes, setDisputes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    if (user) {
      fetchDisputes();
      fetchStats();
    }
  }, [user, activeTab]);

  const fetchDisputes = async () => {
    setLoading(true);
    try {
      const status = activeTab !== 'all' ? activeTab : undefined;
      const res = await API.get('/disputes/my-disputes', { params: { status } });
      setDisputes(res.data.disputes);
    } catch (err) {
      console.error('Failed to fetch disputes:', err);
    }
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const res = await API.get('/disputes/stats');
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  if (!user) {
    return (
      <main className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Sign In Required</CardTitle>
            <CardDescription>Please sign in to access your disputes</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/login">
              <Button className="w-full">Sign In</Button>
            </Link>
          </CardContent>
        </Card>
      </main>
    );
  }

  return (
    <main data-testid="disputes-page" className="min-h-screen bg-background py-8 px-4">
      <SEO title="My Disputes" description="Manage your transaction disputes on AutoTrust Ghana" />
      
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold">Dispute Resolution</h1>
            <p className="text-muted-foreground">Manage and track your transaction disputes</p>
          </div>
          
          <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
            <DialogTrigger asChild>
              <Button className="bg-gold text-black hover:brightness-110 gap-2">
                <Plus className="w-4 h-4" />
                Open Dispute
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <CreateDisputeForm onSuccess={() => { setShowCreateModal(false); fetchDisputes(); }} />
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{stats.total || 0}</div>
                <div className="text-sm text-muted-foreground">Total Disputes</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-yellow-400">{stats.active || stats.open || 0}</div>
                <div className="text-sm text-muted-foreground">Active</div>
              </CardContent>
            </Card>
            {stats.under_review !== undefined && (
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-purple-400">{stats.under_review}</div>
                  <div className="text-sm text-muted-foreground">Under Review</div>
                </CardContent>
              </Card>
            )}
            {stats.resolved !== undefined && (
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-green-400">{stats.resolved}</div>
                  <div className="text-sm text-muted-foreground">Resolved</div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="open">Open</TabsTrigger>
            <TabsTrigger value="under_review">Under Review</TabsTrigger>
            <TabsTrigger value="resolved">Resolved</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-gold" />
              </div>
            ) : disputes.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Scale className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-medium mb-2">No disputes found</h3>
                  <p className="text-sm text-muted-foreground">
                    {activeTab === 'all' 
                      ? "You haven't opened any disputes yet" 
                      : `No ${activeTab.replace('_', ' ')} disputes`}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {disputes.map((dispute, i) => (
                  <motion.div
                    key={dispute.dispute_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <DisputeCard dispute={dispute} />
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Help Section */}
        <Card className="bg-card/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="w-5 h-5 text-gold" />
              How Dispute Resolution Works
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-4 gap-4 text-sm">
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-gold/10 text-gold flex items-center justify-center font-bold shrink-0">1</div>
                <div>
                  <div className="font-medium">Open Dispute</div>
                  <div className="text-muted-foreground">Describe your issue with evidence</div>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-gold/10 text-gold flex items-center justify-center font-bold shrink-0">2</div>
                <div>
                  <div className="font-medium">Other Party Responds</div>
                  <div className="text-muted-foreground">They can provide their side</div>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-gold/10 text-gold flex items-center justify-center font-bold shrink-0">3</div>
                <div>
                  <div className="font-medium">AI Analysis</div>
                  <div className="text-muted-foreground">Our system reviews the case</div>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-gold/10 text-gold flex items-center justify-center font-bold shrink-0">4</div>
                <div>
                  <div className="font-medium">Resolution</div>
                  <div className="text-muted-foreground">Fair outcome for both parties</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}

// Dispute Card Component
function DisputeCard({ dispute }) {
  return (
    <Link to={`/disputes/${dispute.dispute_id}`}>
      <Card className="hover:border-gold/30 transition-colors cursor-pointer">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              {/* Listing Image */}
              <div className="w-16 h-16 rounded-lg bg-muted overflow-hidden shrink-0">
                {dispute.listing?.photos?.[0] ? (
                  <img 
                    src={dispute.listing.photos[0]} 
                    alt={dispute.listing.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <FileText className="w-6 h-6 text-muted-foreground" />
                  </div>
                )}
              </div>
              
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Badge className={STATUS_COLORS[dispute.status] || STATUS_COLORS.open}>
                    {dispute.status?.replace('_', ' ')}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {dispute.user_role === 'buyer' ? 'You are the buyer' : 'You are the seller'}
                  </span>
                </div>
                <h3 className="font-medium">{dispute.listing?.title || 'Transaction Dispute'}</h3>
                <p className="text-sm text-muted-foreground">
                  {REASON_LABELS[dispute.reason] || dispute.reason} • vs {dispute.other_party}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="font-semibold">GHS {dispute.amount?.toLocaleString()}</div>
                <div className="text-xs text-muted-foreground">
                  {new Date(dispute.created_at).toLocaleDateString()}
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

// Create Dispute Form
function CreateDisputeForm({ onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [form, setForm] = useState({
    transaction_id: '',
    reason: '',
    description: ''
  });

  useEffect(() => {
    fetchTransactions();
    fetchReasons();
  }, []);

  const fetchTransactions = async () => {
    try {
      // Get user's escrow transactions that can be disputed
      const res = await API.get('/escrow/transactions');
      const disputeable = res.data.transactions?.filter(t => 
        ['funded', 'in_progress', 'delivered'].includes(t.status)
      ) || [];
      setTransactions(disputeable);
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
    }
  };

  const fetchReasons = async () => {
    try {
      const res = await API.get('/disputes/reasons');
      setReasons(res.data.reasons);
    } catch (err) {
      console.error('Failed to fetch reasons:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.transaction_id || !form.reason || !form.description) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await API.post('/disputes/create', form);
      toast.success('Dispute created successfully');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create dispute');
    }
    setLoading(false);
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-yellow-500" />
          Open a Dispute
        </DialogTitle>
        <DialogDescription>
          Describe your issue and we'll help resolve it fairly
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-4 mt-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Transaction</label>
          <Select value={form.transaction_id} onValueChange={(v) => setForm({...form, transaction_id: v})}>
            <SelectTrigger>
              <SelectValue placeholder="Select a transaction" />
            </SelectTrigger>
            <SelectContent>
              {transactions.length === 0 ? (
                <SelectItem value="__none__" disabled>No disputeable transactions</SelectItem>
              ) : (
                transactions.map(t => (
                  <SelectItem key={t.transaction_id} value={t.transaction_id}>
                    {t.listing_title || t.transaction_id} - GHS {t.amount?.toLocaleString()}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Reason</label>
          <Select value={form.reason} onValueChange={(v) => setForm({...form, reason: v})}>
            <SelectTrigger>
              <SelectValue placeholder="Select a reason" />
            </SelectTrigger>
            <SelectContent>
              {reasons.map(r => (
                <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Description</label>
          <Textarea
            placeholder="Describe your issue in detail. Include dates, communications, and any relevant information..."
            value={form.description}
            onChange={(e) => setForm({...form, description: e.target.value})}
            rows={4}
          />
        </div>

        <Button type="submit" disabled={loading} className="w-full bg-gold text-black hover:brightness-110">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          Submit Dispute
        </Button>
      </form>
    </>
  );
}

// Dispute Detail Page
export function DisputeDetail() {
  const { disputeId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dispute, setDispute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState(false);
  const [response, setResponse] = useState('');

  useEffect(() => {
    fetchDispute();
  }, [disputeId]);

  const fetchDispute = async () => {
    setLoading(true);
    try {
      const res = await API.get(`/disputes/${disputeId}`);
      setDispute(res.data.dispute);
    } catch (err) {
      toast.error('Failed to load dispute');
      navigate('/disputes');
    }
    setLoading(false);
  };

  const submitResponse = async () => {
    if (!response.trim()) {
      toast.error('Please enter your response');
      return;
    }
    
    setResponding(true);
    try {
      await API.post(`/disputes/${disputeId}/respond`, { response });
      toast.success('Response submitted');
      fetchDispute();
      setResponse('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit response');
    }
    setResponding(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gold" />
      </div>
    );
  }

  if (!dispute) return null;

  const canRespond = dispute.user_role !== dispute.initiator_role && 
                     !dispute.seller_response && 
                     dispute.status !== 'resolved';

  return (
    <main className="min-h-screen bg-background py-8 px-4">
      <SEO title={`Dispute: ${dispute.listing?.title || disputeId}`} />
      
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Back Button */}
        <Link to="/disputes" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-gold">
          <ArrowLeft className="w-4 h-4" />
          Back to Disputes
        </Link>

        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Badge className={STATUS_COLORS[dispute.status]}>
                {dispute.status?.replace('_', ' ')}
              </Badge>
              <span className="text-sm text-muted-foreground">
                Dispute #{dispute.dispute_id?.slice(-8)}
              </span>
            </div>
            <h1 className="text-2xl font-bold">{dispute.listing?.title || 'Transaction Dispute'}</h1>
            <p className="text-muted-foreground">{REASON_LABELS[dispute.reason]}</p>
          </div>
          
          <Card className="bg-gold/10 border-gold/20">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold">GHS {dispute.amount?.toLocaleString()}</div>
              <div className="text-sm text-muted-foreground">Disputed Amount</div>
            </CardContent>
          </Card>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="md:col-span-2 space-y-6">
            {/* Original Complaint */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="w-5 h-5" />
                  {dispute.initiator_role === 'buyer' ? 'Buyer' : 'Seller'}'s Complaint
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap">{dispute.description}</p>
                {dispute.evidence_urls?.length > 0 && (
                  <div className="mt-4">
                    <div className="text-sm font-medium mb-2">Evidence ({dispute.evidence_urls.length})</div>
                    <div className="flex gap-2 flex-wrap">
                      {dispute.evidence_urls.map((url, i) => (
                        <a key={i} href={url} target="_blank" rel="noopener" className="text-gold text-sm hover:underline">
                          Evidence {i + 1}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Response */}
            {dispute.seller_response ? (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MessageSquare className="w-5 h-5" />
                    {dispute.initiator_role === 'buyer' ? 'Seller' : 'Buyer'}'s Response
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm whitespace-pre-wrap">{dispute.seller_response}</p>
                </CardContent>
              </Card>
            ) : canRespond ? (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Your Response</CardTitle>
                  <CardDescription>Provide your side of the story</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Textarea
                    placeholder="Explain your perspective on this dispute..."
                    value={response}
                    onChange={(e) => setResponse(e.target.value)}
                    rows={4}
                  />
                  <Button onClick={submitResponse} disabled={responding} className="bg-gold text-black hover:brightness-110">
                    {responding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Submit Response
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-muted/50">
                <CardContent className="py-6 text-center text-muted-foreground">
                  <Clock className="w-8 h-8 mx-auto mb-2" />
                  Waiting for the other party to respond
                </CardContent>
              </Card>
            )}

            {/* Resolution */}
            {dispute.resolution && (
              <Card className="border-green-500/30 bg-green-500/5">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 text-green-400">
                    <CheckCircle className="w-5 h-5" />
                    Resolution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm mb-2">{dispute.resolution.notes}</p>
                  {dispute.resolution.winner && (
                    <Badge variant="outline" className="border-green-500/30 text-green-400">
                      Resolved in favor of {dispute.resolution.winner}
                    </Badge>
                  )}
                  {dispute.resolution.refund_amount && (
                    <p className="text-sm mt-2">Refund: GHS {dispute.resolution.refund_amount.toLocaleString()}</p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Parties */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Parties Involved</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Buyer</span>
                  <span className="text-sm font-medium">{dispute.buyer_info?.name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Seller</span>
                  <span className="text-sm font-medium">{dispute.seller_info?.name}</span>
                </div>
              </CardContent>
            </Card>

            {/* Timeline */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Timeline</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dispute.timeline?.map((event, i) => (
                    <div key={i} className="flex gap-3 text-sm">
                      <div className="w-2 h-2 rounded-full bg-gold mt-1.5 shrink-0" />
                      <div>
                        <div className="font-medium">{event.details}</div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(event.timestamp).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Info */}
            <Card>
              <CardContent className="pt-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Opened</span>
                  <span>{new Date(dispute.created_at).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Days Active</span>
                  <span>{dispute.days_since_purchase || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Your Role</span>
                  <span className="capitalize">{dispute.user_role}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </main>
  );
}
